import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TreeItem, TreeItemIndex } from 'react-complex-tree';

import { TQModelDataProvider } from 'components/TQModelTree/data-providers/TQModelDataProvider';
import { DATA_OBJECT_TERRITORY } from 'constants/tq';
import { useFeatureFlags } from 'contexts/FeatureFlags';
import { AllViewsTerritoryObjects, useTerritoryObjectsFromAllViews } from 'hooks/useTerritoryObjectsFromAllViews';
import { useQueryParams } from 'hooks/useQueryParams';
import { AllViewsTerritoryDataObjectsResponse } from 'services/territoryDataObjects';
import { TerritoryView, TerritoryViewsService } from 'services/territoryViews';
import { StringifiedUUID } from 'types';
import { TerritoryDataObject, TQModelTreeNode } from 'types/tqModelTree';
import { UNASSIGNED_ID } from 'utils/tree';

import { useNavigationData } from 'hooks/useNavigationData';
import { TQRouteQueryParams, useTQRouteContext } from './TQRouteContext';

export type TerritoryViewContextType = {
  // Territory view selection state
  selectedTerritoryViewId: StringifiedUUID | null;
  setSelectedTerritoryViewId: (viewId: StringifiedUUID | null) => void;
  selectedTerritoryView: TerritoryView | null;
  territoryViews: TerritoryView[];
  areTerritoryViewsLoading: boolean;
  invalidateTerritoryViews: () => void;

  // Local tree state
  treeData: Record<TreeItemIndex, TreeItem<TQModelTreeNode>> | null;
  setTreeData: Dispatch<SetStateAction<Record<TreeItemIndex, TreeItem<TQModelTreeNode>> | null>>;
  rootNode: TQModelTreeNode | null;
  setRootNode: Dispatch<SetStateAction<TQModelTreeNode | null>>;
  currentNode: TQModelTreeNode | null;
  setCurrentNode: Dispatch<SetStateAction<TQModelTreeNode | null>>;
  activeNode: TQModelTreeNode | null;
  setActiveNode: Dispatch<SetStateAction<TQModelTreeNode | null>>;
  tqDataProvider: TQModelDataProvider | undefined;
  setTQDataProvider: Dispatch<SetStateAction<TQModelDataProvider | undefined>>;

  // Territory objects - current view
  territoriesObjects: Record<StringifiedUUID, TerritoryDataObject>;
  setTerritoriesObjects: Dispatch<SetStateAction<Record<StringifiedUUID, TerritoryDataObject>>>;

  // Territory objects - for all territory views
  allTerritoriesObjectsMap: AllViewsTerritoryObjects;
  allViewsTerritoryObjectsData: AllViewsTerritoryDataObjectsResponse | undefined;
  getTerritoriesForView: (viewId: StringifiedUUID | null) => Record<StringifiedUUID, TerritoryDataObject>;
  areAllTerritoriesViewsLoading: boolean;

  // Utilities
  getActiveTerritory: () => TerritoryDataObject | null;
};

const TerritoryViewContext = createContext<TerritoryViewContextType | null>(null);

export const useTerritoryViewContext = (): TerritoryViewContextType => {
  const context = useContext(TerritoryViewContext);
  if (!context) {
    throw new Error('useTerritoryViewContext must be used within a TerritoryViewContextProvider');
  }
  return context;
};

type Props = {
  children: ReactNode;
  activeNode?: TQModelTreeNode | null;
  territoriesObjects?: Record<StringifiedUUID, TerritoryDataObject>;
};

export const TerritoryViewContextProvider = ({
  children,
  activeNode: initialActiveNode = null,
  territoriesObjects: initialTerritoriesObjects = {},
}: Props) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isTerritoryOverlaysEnabled = isFeatureEnabled('feature_flag_spm_territory_overlays');
  const { tqModel } = useNavigationData();
  const { territoryViewIdQueryParam } = useTQRouteContext();
  const { setQueryParam, deleteQueryParam } = useQueryParams();
  const [selectedTerritoryViewId, _setSelectedTerritoryViewId] = useState<StringifiedUUID | null>(null);

  // Tree state
  const [treeData, setTreeData] = useState<Record<TreeItemIndex, TreeItem<TQModelTreeNode>> | null>(null);
  const [rootNode, setRootNode] = useState<TQModelTreeNode | null>(null);
  const [currentNode, setCurrentNode] = useState<TQModelTreeNode | null>(null);
  const [activeNode, setActiveNode] = useState<TQModelTreeNode | null>(initialActiveNode);
  const [tqDataProvider, setTQDataProvider] = useState<TQModelDataProvider>();

  useEffect(() => {
    _setSelectedTerritoryViewId(territoryViewIdQueryParam);
  }, [territoryViewIdQueryParam]);

  const setSelectedTerritoryViewId = useCallback(
    (viewId: StringifiedUUID | null) => {
      setCurrentNode(null);
      setActiveNode(null);
      if (viewId) {
        setQueryParam(TQRouteQueryParams.TERRITORY_VIEW_ID, viewId);
      } else {
        deleteQueryParam(TQRouteQueryParams.TERRITORY_VIEW_ID);
      }
    },
    [setQueryParam, deleteQueryParam],
  );

  const { query: territoryViewsQuery, invalidateQuery: invalidateTerritoryViews } =
    TerritoryViewsService.useGetTerritoryViewsQuery(tqModel?.id, {
      enabled: isTerritoryOverlaysEnabled && !!tqModel?.id,
    });

  const territoryViews = useMemo(() => territoryViewsQuery.data ?? [], [territoryViewsQuery.data]);

  const selectedTerritoryView = useMemo(() => {
    if (!selectedTerritoryViewId) return null;
    return territoryViews.find((v) => v.uuid === selectedTerritoryViewId) ?? null;
  }, [selectedTerritoryViewId, territoryViews]);

  const {
    allTerritoriesObjectsMap,
    allViewsTerritoryObjectsData,
    isLoading: areAllTerritoriesViewsLoading,
  } = useTerritoryObjectsFromAllViews({
    tqModelId: tqModel?.id,
    enabled: isTerritoryOverlaysEnabled,
  });

  const getTerritoriesForView = useCallback(
    (viewId: StringifiedUUID | null): Record<StringifiedUUID, TerritoryDataObject> => {
      const key = viewId ?? 'main';
      return allTerritoriesObjectsMap[key] ?? {};
    },
    [allTerritoriesObjectsMap],
  );

  const [territoriesObjects, setTerritoriesObjects] =
    useState<Record<StringifiedUUID, TerritoryDataObject>>(initialTerritoriesObjects);

  const getActiveTerritory = useCallback((): TerritoryDataObject | null => {
    if (activeNode?.id === UNASSIGNED_ID) {
      return {
        id: '',
        external_id: UNASSIGNED_ID,
        hierarchy_model_id: tqModel?.id ?? '',
        position: 0,
        is_root: false,
        name: 'Unassigned Accounts',
        object_type: DATA_OBJECT_TERRITORY,
      };
    }
    if (activeNode) {
      return territoriesObjects[activeNode.id as StringifiedUUID] ?? null;
    }
    return null;
  }, [activeNode, territoriesObjects, tqModel?.id]);

  const contextValue = useMemo(
    () => ({
      selectedTerritoryViewId,
      setSelectedTerritoryViewId,
      selectedTerritoryView,
      territoryViews,
      areTerritoryViewsLoading: territoryViewsQuery.isLoading,
      invalidateTerritoryViews,

      treeData,
      setTreeData,
      rootNode,
      setRootNode,
      currentNode,
      setCurrentNode,
      activeNode,
      setActiveNode,
      tqDataProvider,
      setTQDataProvider,

      territoriesObjects,
      setTerritoriesObjects,

      allTerritoriesObjectsMap,
      allViewsTerritoryObjectsData,
      getTerritoriesForView,
      areAllTerritoriesViewsLoading,

      getActiveTerritory,
    }),
    [
      selectedTerritoryViewId,
      setSelectedTerritoryViewId,
      selectedTerritoryView,
      territoryViews,
      territoryViewsQuery.isLoading,
      invalidateTerritoryViews,
      treeData,
      rootNode,
      currentNode,
      activeNode,
      tqDataProvider,
      territoriesObjects,
      allTerritoriesObjectsMap,
      allViewsTerritoryObjectsData,
      getTerritoriesForView,
      areAllTerritoriesViewsLoading,
      getActiveTerritory,
    ],
  );

  return <TerritoryViewContext.Provider value={contextValue}>{children}</TerritoryViewContext.Provider>;
};
